import { useEffect, useRef } from "react";

type ShortcutOptions = {
  /**
   * Ignore the shortcut while the user is typing. For shortcuts that only
   * rearrange the page, so they can't swallow a keystroke meant for the text.
   */
  skipWhileTyping?: boolean;
  /**
   * Listen in the capture phase and stop propagation, so descendants with their
   * own binding for the key (e.g. a rich-text editor's Mod-<key>) never see it.
   */
  capture?: boolean;
  /** Return false to let the keystroke through — use to scope the shortcut. */
  enabled?: () => boolean;
};

/**
 * Binds ⌘/Ctrl+`key` on the document while the component is mounted.
 *
 * The handler is read from a ref, so it sees the current render's state without
 * re-registering the listener. Pair the shortcut with `aria-keyshortcuts` on the
 * button it stands in for, plus a
 * [`ShortcutHint`](../components/ui-kit/shortcut-hint.tsx) label.
 */
export function useShortcut(
  key: string,
  handler: () => void,
  options: ShortcutOptions = {},
) {
  const latest = useRef({ handler, options });
  latest.current = { handler, options };

  // Read here rather than inside the listener: it decides which phase to
  // register in, so a change has to re-run the effect.
  const capture = options.capture ?? false;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const { handler, options } = latest.current;
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== key) {
        return;
      }
      if (options.skipWhileTyping && isTypingTarget(e.target)) {
        return;
      }
      if (options.enabled && !options.enabled()) {
        return;
      }
      e.preventDefault();
      if (capture) {
        e.stopPropagation();
      }
      handler();
    }

    document.addEventListener("keydown", handleKeyDown, capture);
    return () =>
      document.removeEventListener("keydown", handleKeyDown, capture);
  }, [key, capture]);
}

/** Whether the keystroke landed in a text field or rich-text editor. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return Boolean(
    el?.isContentEditable || el?.closest("input, textarea, select"),
  );
}
